import { Service, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

/**
 * Treats a route's `title` as a translation key and keeps `<title>` in sync
 * with both the active route and the active language.
 *
 * Routes declare `title: 'dashboard.title'`; the document title becomes
 * "Module · SAPHIR Web". Module routes declare their manifest's `titleKey`, so
 * a module names the tab without knowing anything about the shell.
 */
@Service()
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);

  /** Translation key of the route currently shown, `null` for untitled routes. */
  private readonly activeTitleKey = signal<string | null>(null);

  constructor() {
    super();

    // `currentLang` is a signal, so this re-runs when the language resolves.
    effect(() => {
      this.translate.currentLang();

      this.title.setTitle(this.composeTitle(this.activeTitleKey()));
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.activeTitleKey.set(this.buildTitle(snapshot) ?? null);
  }

  private composeTitle(titleKey: string | null): string {
    const appName = this.translate.instant('app.title') as string;

    if (!titleKey) {
      return appName;
    }

    const pageTitle = this.translate.instant(titleKey) as string;

    return `${pageTitle} · ${appName}`;
  }
}
